import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Registre global des formulaires modifiés et non enregistrés.
const dirty = new Set();
const subs = new Set();
let last = null;
let pending = null;
const emit = () => subs.forEach((f) => f());

export const guard = {
  mark(id) { if (!dirty.has(id)) { dirty.add(id); emit(); } },
  clear(id) { if (dirty.delete(id)) emit(); },
  clearAll() { if (dirty.size) { dirty.clear(); emit(); } },
  touch(id) { last = id; },
  saved() { if (last) guard.clear(last); },
  is: (id) => dirty.has(id),
  any: () => dirty.size > 0,
};

export function confirmLeave() {
  if (!pending) { pending = {}; pending.promise = new Promise((r) => { pending.resolve = r; }); emit(); }
  return pending.promise;
}
const answer = (ok) => { const p = pending; pending = null; emit(); p?.resolve(ok); };

const CLOSE_LABELS = ["Annuler", "Fermer"];
// Les options des listes déroulantes sont rendues hors du dialogue : on mémorise le dialogue qui a ouvert la liste.
let comboOwner = null;
const onOptionPick = (e) => {
  if (e.type === "keydown" && !["Enter", " "].includes(e.key)) return;
  const o = e.target.closest?.("[role=option]");
  if (!o || !comboOwner || o.getAttribute("aria-selected") === "true" || comboOwner.explicit()) return;
  guard.mark(comboOwner.id);
};
if (typeof document !== "undefined") {
  document.addEventListener("pointerup", onOptionPick, true);
  document.addEventListener("keydown", onOptionPick, true);
}
const isField = (t) => t?.matches?.("input,textarea,select,[contenteditable=true]")
  && t.type !== "search" && !t.closest("[data-guard-ignore]") && !(t.placeholder || "").toLowerCase().startsWith("rech");

// Branche le suivi des saisies sur un élément DOM (dialogue ou page).
export function attachTracking(el, id, { explicit, isDirty, onLeave }) {
  const onEdit = (e) => { if (!explicit() && isField(e.target)) guard.mark(id); };
  const onDown = (e) => { guard.touch(id); if (e.target.closest?.("[role=combobox]")) comboOwner = { id, explicit }; };
  const onClick = (e) => {
    const b = e.target.closest?.("button,[role=switch],[role=checkbox],[role=radio]");
    if (!b || !el.contains(b)) return;
    if (!explicit() && ["switch", "checkbox", "radio"].includes(b.getAttribute("role"))) guard.mark(id);
    if (b.dataset.guardBypass || !onLeave || !CLOSE_LABELS.includes(b.textContent.trim()) || !isDirty()) return;
    e.preventDefault(); e.stopPropagation();
    confirmLeave().then((ok) => { if (ok) { guard.clear(id); onLeave(b); } });
  };
  el.addEventListener("input", onEdit, true);
  el.addEventListener("change", onEdit, true);
  el.addEventListener("pointerdown", onDown, true);
  el.addEventListener("click", onClick, true);
  return () => {
    el.removeEventListener("input", onEdit, true); el.removeEventListener("change", onEdit, true);
    el.removeEventListener("pointerdown", onDown, true); el.removeEventListener("click", onClick, true);
    guard.clear(id);
  };
}

// Protection d'une page entière (paramètres, fiche atelier...).
export function usePageGuard() {
  const cleanup = useRef(null);
  useEffect(() => () => cleanup.current?.(), []);
  return useRef((el) => {
    cleanup.current?.(); cleanup.current = null;
    if (!el) return;
    const id = `page-${Math.random().toString(36).slice(2)}`;
    cleanup.current = attachTracking(el, id, { explicit: () => false, isDirty: () => guard.is(id) });
  }).current;
}

const isGuardState = () => !!window.history.state?.__unsavedGuard;

export function UnsavedChangesPrompt() {
  const [, force] = useState(0);
  const sentinel = useRef(false);
  const ignorePop = useRef(false);
  useEffect(() => {
    const sync = () => {
      force((n) => n + 1);
      if (guard.any() && !sentinel.current) {
        sentinel.current = true;
        window.history.pushState({ ...(window.history.state || {}), __unsavedGuard: 1 }, "", window.location.href);
      } else if (!guard.any() && sentinel.current) {
        sentinel.current = false;
        if (isGuardState()) { ignorePop.current = true; window.history.back(); }
      }
    };
    subs.add(sync);
    const onPop = () => {
      if (ignorePop.current) { ignorePop.current = false; return; }
      if (!sentinel.current) return;
      sentinel.current = false;
      if (!guard.any()) return;
      confirmLeave().then((ok) => {
        if (ok) { guard.clearAll(); window.history.back(); } else { sentinel.current = false; sync(); }
      });
    };
    const onUnload = (e) => { if (guard.any()) { e.preventDefault(); e.returnValue = ""; } };
    const onLink = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || a.dataset.guardBypass || !guard.any() || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      e.preventDefault(); e.stopPropagation();
      confirmLeave().then((ok) => {
        if (!ok) return;
        const go = () => { a.dataset.guardBypass = "1"; a.click(); delete a.dataset.guardBypass; };
        // Retirer d'abord l'entrée sentinelle de l'historique, puis naviguer (sinon history.back() annule la navigation).
        if (sentinel.current && isGuardState()) {
          sentinel.current = false; ignorePop.current = true;
          window.addEventListener("popstate", () => setTimeout(go, 0), { once: true });
          guard.clearAll(); window.history.back();
        } else { guard.clearAll(); go(); }
      });
    };
    window.addEventListener("popstate", onPop);
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onLink, true);
    return () => { subs.delete(sync); window.removeEventListener("popstate", onPop); window.removeEventListener("beforeunload", onUnload); document.removeEventListener("click", onLink, true); };
  }, []);

  return (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o && pending) answer(false); }}>
      <AlertDialogContent className="bg-white border-slate-200 z-[100]" data-testid="unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-slate-900">Modifications non enregistrées</AlertDialogTitle>
          <AlertDialogDescription>Vous avez des modifications qui n'ont pas été enregistrées. Voulez-vous vraiment quitter ?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="unsaved-stay" data-guard-bypass="1" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold border-0">Continuer la modification</AlertDialogCancel>
          <AlertDialogAction data-testid="unsaved-leave" data-guard-bypass="1" onClick={() => answer(true)} className="bg-red-600 hover:bg-red-700 text-white">Quitter sans enregistrer</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
